<template>
  <div class="settings-page">
    <h2>Meetings</h2>
    <div class="subtitle">Manage your meeting preferences.</div>

    <section>
      <div class="entry vertical">
        <div class="label">
          <h3>Join next meeting</h3>
          <p class="description">
            Assign a hotkey to automatically join the next meeting's conference
            call on your agenda.
          </p>
        </div>
        <GlobalHotkeyCapture
          v-if="in_electron"
          class="keyboard-shortcut"
          :hotkey-name="joinHotkeyIdentifier"
        />
        <BaseAlert v-else state="info" size="small">
          ⚠️ This hotkey can only be assigned in the
          <a
            class="download-link"
            href="https://routine.co/download"
            target="_blank"
            >desktop app</a
          >.
        </BaseAlert>
      </div>
      <div class="entry">
        <div class="preference-copy">
          <label>Sound reminder</label>
          <div class="note">
            Play a sound reminder one minute before the event starts.
          </div>
        </div>
        <BaseToggleButton
          :checked="preEventSound"
          @change="togglePreEventSound"
        />
      </div>
      <div class="entry">
        <div class="preference-copy">
          <label>Meeting detection</label>
          <div class="note">
            Get prompted to start recording a meeting when Routine detects that
            you have joined a conferencing call.
          </div>
        </div>
        <DownloadToggle
          v-if="!in_electron"
          :checked="false"
          :message="DOWNLOAD_MESSAGE"
          @change="toggleMeetingDetection"
        />
        <UpgradeToggle
          v-else
          :checked="meetingDetection"
          entitlement-key="ai-meeting-notes"
          @change="toggleMeetingDetection"
        />
      </div>
      <div class="entry">
        <div class="preference-copy">
          <label>Automatic meeting recording</label>
          <div class="note">
            Automatically start the recording of meetings when opened.
          </div>
        </div>
        <DownloadToggle
          v-if="!in_electron"
          :checked="false"
          :message="DOWNLOAD_MESSAGE"
          @change="toggleAIMeetingRecording"
        />
        <UpgradeToggle
          v-else
          :checked="isAIMeetingRecordingEnabled"
          entitlement-key="ai-meeting-notes"
          @change="toggleAIMeetingRecording"
        />
      </div>
      <div class="entry vertical language-entry">
        <div class="preference-copy">
          <label>Language</label>
          <div class="note">
            Language used to transcribe meeting recordings and voice input.
            Automatic detects and switches among 10 languages in the same
            meeting (English, French, Spanish, German, Hindi, Italian, Japanese,
            Dutch, Portuguese, and Russian). Choose a specific language if yours
            is not in that set.
          </div>
        </div>
        <BaseInputSelect
          v-model:value="transcriptionLanguage"
          class="language-select"
          :options="transcriptionLanguageOptions"
          select-only
          searchable
        />
      </div>
      <div class="entry vertical automation-entry">
        <div class="preference-copy">
          <label>Meeting automation</label>
          <div class="note">
            Run an AI automation on the event/object once the transcript has
            been saved to its notes.
          </div>
        </div>
        <div class="automation-controls">
          <AutomationPicker
            class="automation-select"
            :value="selectedAutomationId"
            include-assistant
            placeholder="No automation"
            clearable
            show-open-button
            @update:value="onSelectAutomation"
          />
          <BaseButton
            kind="auxiliary"
            :state="isCreatingDefaultAutomation ? 'loading' : 'default'"
            tooltip="Restore the default automation"
            tooltip-display="always"
            @click="onResetAutomation"
          >
            <template #icon>
              <ResetIcon />
            </template>
          </BaseButton>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from "vue";

import AutomationPicker from "@/components/ai/AutomationPicker.vue";
import type { Automation } from "@/components/ai/useAutomations";
import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseInputSelect, {
  type SelectOption,
} from "@/components/base/BaseInputSelect.vue";
import BaseToggleButton from "@/components/base/BaseToggleButton.vue";
import DownloadToggle from "@/components/base/DownloadToggle.vue";
import UpgradeToggle from "@/components/base/UpgradeToggle.vue";
import GlobalHotkeyCapture from "@/components/GlobalHotkeyCapture.vue";
import ResetIcon from "@/components/icons/ResetIcon.vue";
import * as inject from "@/components/inject";
import { STORAGE_KEY } from "@/composables/recording/useAutoMeetingRecording";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { useMeetingDetection } from "@/composables/useMeetingDetection";
import { useSoundReminder } from "@/composables/useSoundReminder";
import {
  createDefaultMeetingAutomation,
  getSelectedMeetingAutomationId,
  setSelectedMeetingAutomationId,
  unsetSelectedMeetingAutomationId,
  validateMeetingAutomationId,
} from "@/recording/automation";
import {
  DEFAULT_MEETING_TRANSCRIPTION_LANGUAGE,
  MEETING_TRANSCRIPTION_LANGUAGE_KEY,
  MEETING_TRANSCRIPTION_LANGUAGES,
  parseMeetingTranscriptionLanguage,
} from "@/recording/languages";
import { useReleaser } from "@/ref";
import { hotkeys } from "@/utils/hotkeys";
import { in_electron } from "@/utils/in";

const DOWNLOAD_MESSAGE =
  "Auto AI meeting recording is only supported in the Desktop apps.";

export default defineComponent({
  components: {
    AutomationPicker,
    BaseAlert,
    BaseButton,
    BaseInputSelect,
    BaseToggleButton,
    DownloadToggle,
    GlobalHotkeyCapture,
    ResetIcon,
    UpgradeToggle,
  },
  setup() {
    const profile = inject.profile();
    const controller = inject.controller();
    const baseLogger = inject.logger();
    const automations = inject.automations();
    const logger = baseLogger.forModule("settings/meetings");

    const releaser = useReleaser();

    const { preEventSound, togglePreEventSound } = useSoundReminder(profile);
    const { meetingDetection, toggleMeetingDetection } =
      useMeetingDetection(profile);

    // Namespaced per account (adopting the values written un-namespaced by
    // older versions on first read).
    const { migrate, syncState } = useBrowserStore(
      profile.value.user.accountId
    );
    migrate(STORAGE_KEY);
    migrate(MEETING_TRANSCRIPTION_LANGUAGE_KEY);
    const autoMeetingRecording = releaser.add(
      syncState<boolean>(STORAGE_KEY, true)
    );

    // Display the stored preference regardless of the entitlement: if the
    // entitlement is lost while the feature is on, the toggle must keep
    // showing "on" so the user can still turn it off (only enabling is gated,
    // by UpgradeToggle). Auto-recording itself re-checks the entitlement at
    // runtime in useAutoMeetingRecording().
    const isAIMeetingRecordingEnabled = computed(
      () => in_electron && autoMeetingRecording.value
    );

    const toggleAIMeetingRecording = (value: boolean) => {
      autoMeetingRecording.value = value;
    };

    const transcriptionLanguage = releaser.add(
      syncState<string>(
        MEETING_TRANSCRIPTION_LANGUAGE_KEY,
        DEFAULT_MEETING_TRANSCRIPTION_LANGUAGE
      )
    );
    transcriptionLanguage.value = parseMeetingTranscriptionLanguage(
      transcriptionLanguage.value
    );
    const transcriptionLanguageOptions: SelectOption[] =
      MEETING_TRANSCRIPTION_LANGUAGES.map((language) => ({
        value: language.code,
        label: language.label,
        aliases:
          language.code === "multi" ? ["multi", "auto"] : [language.code],
      }));

    // ── Meeting automation ──────────────────────────────────────────────────
    // The automation must belong to the assistant so it can operate on the
    // recorded object regardless of the organization it lives in.

    // The selection is persisted explicitly at each write site rather than
    // through a synced ref or a write-back watcher, because the store value
    // distinguishes *why* it was cleared: a user action persists through
    // setSelectedMeetingAutomationId — including "" when the picker is
    // cleared, the deliberate "No automation" choice that is never re-seeded
    // — while the validations below discard an id that points at nothing by
    // unsetting the key entirely, so the account reads as never initialized
    // and the launch-time setDefaultMeetingAutomation seeds a fresh default.
    // A never-initialized selection (undefined) displays as "no automation",
    // like a disabled one.
    const selectedAutomationId = ref(
      getSelectedMeetingAutomationId(profile) ?? ""
    );

    // Discard a stored id that positively resolves to nothing: clear the
    // picker and return the store key to "never initialized" (not the user's
    // "" — see above).
    const discardStaleSelection = () => {
      selectedAutomationId.value = "";
      unsetSelectedMeetingAutomationId(profile);
    };

    const assistantAutomations = computed<Automation[]>(() => {
      const value = automations.value;
      const assistant = value?.assistant.value;
      if (!value || !assistant) return [];
      const byAutomation =
        value.all.value?.[assistant.organizationId]?.[assistant.agentId] ?? {};
      return Object.values(byAutomation).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
    });

    const onSelectAutomation = (value: string) => {
      selectedAutomationId.value = value;
      setSelectedMeetingAutomationId(profile, value);
    };

    // The stored id may not resolve to an automation of this account — it
    // can come from another account of this profile through the legacy
    // un-namespaced key, or the automation was deleted. Verify it against
    // the controller and clear the selection when it points at nothing
    // (otherwise the picker would display the raw id instead of a name).
    void (async () => {
      const loadedId = selectedAutomationId.value;
      if (!loadedId) return;
      const valid = await validateMeetingAutomationId(
        controller,
        profile,
        loadedId
      );
      // Only discard the id that was verified: the user may have picked
      // another automation in the meantime.
      if (!valid && selectedAutomationId.value === loadedId) {
        discardStaleSelection();
      }
    })().catch((error) => {
      // Background check only: a failure must never take the settings page
      // down with a fatal, the selection is simply left as-is.
      logger.error("failed to validate the selected meeting automation", {
        error,
      });
    });

    // The selected automation may also be deleted while this page is open:
    // once the assistant's automations are known, reset a stale selection so
    // the stored id doesn't point at nothing forever. Only reset against a
    // non-empty list: right after the assistant resolves the list is still
    // loading and looks empty, and clearing then would wipe a valid
    // selection (e.g. the one seeded at sign-up).
    watch(
      [assistantAutomations, () => automations.value?.assistant.value],
      ([list, assistant]) => {
        if (!assistant || list.length === 0) return;
        const id = selectedAutomationId.value;
        if (id && !list.some((automation) => automation.id === id)) {
          discardStaleSelection();
        }
      },
      { immediate: true }
    );

    // Reset: create a brand-new assistant automation with the default
    // summary prompt and select it. Deliberately never reuses an existing
    // automation, so a reset always starts from a pristine prompt.
    const isCreatingDefaultAutomation = ref(false);
    const onResetAutomation = async () => {
      if (isCreatingDefaultAutomation.value) return;
      isCreatingDefaultAutomation.value = true;
      try {
        const automationId = await createDefaultMeetingAutomation(
          controller,
          baseLogger,
          profile
        );
        selectedAutomationId.value = automationId;
        setSelectedMeetingAutomationId(profile, automationId);
      } catch (error) {
        logger.error("failed to create the default meeting automation", {
          error,
        });
      } finally {
        isCreatingDefaultAutomation.value = false;
      }
    };

    return {
      DOWNLOAD_MESSAGE,
      in_electron,
      joinHotkeyIdentifier: hotkeys.STORE_IDENTIFIERS.JOIN,
      isAIMeetingRecordingEnabled,
      meetingDetection,
      preEventSound,
      selectedAutomationId,
      onSelectAutomation,
      transcriptionLanguage,
      transcriptionLanguageOptions,
      isCreatingDefaultAutomation,
      onResetAutomation,
      toggleAIMeetingRecording,
      toggleMeetingDetection,
      togglePreEventSound,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss";

.download-link {
  // Same blue as the "New" badge ($tertiary-50) instead of the browser's
  // default link color.
  @include themed() {
    color: t($tertiary);
  }
}

.note {
  @extend %font-caption-regular;
  opacity: 0.75;

  code {
    @extend %font-small-regular;
    padding: 1px 4px;
    border: 1px solid;
    border-radius: $border-radius-s;

    @include themed() {
      color: t($grey-60);
      border-color: t($grey-15);
    }
  }
}

.automation-entry,
.language-entry {
  align-items: flex-start;
  gap: 12px;
}

.automation-controls {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  max-width: 280px;

  .automation-select {
    flex: 1;
    min-width: 0;
  }
}

.language-select {
  width: 100%;
  max-width: 280px;
}
</style>
